import React from "react";
import { useNavigate } from "react-router-dom";
import { ProTrackMark } from "../components/ProTrackLogo";

const LAST_UPDATED = "September 23, 2026";

const SECTIONS = [
  {
    h: "1. What this policy covers",
    body: `This Privacy Policy explains what personal and company information ProTrackNG (operated by ProStack NG Technologies, Port Harcourt, Rivers State, Nigeria) collects when you use the platform, why we collect it, who we share it with, and the choices and rights you have over it. This policy is written with Nigeria's Data Protection Act 2023 and the Nigeria Data Protection Regulation in mind, alongside common international practice.`,
  },
  {
    h: "2. Information we collect",
    body: `Account & company details: full name, email, phone number, password (stored hashed, never in plain text), company name, CAC/RC or BN registration number, industry, and state of operation. Preferences: tender categories and states you choose to track, used to power AI-matched recommendations. Alert settings: your WhatsApp number, if you opt in to WhatsApp alerts, and your email-alert preference. Billing information: subscription plan and payment status. Card details are entered directly with Paystack, our payment processor — ProTrackNG does not receive or store your full card number. Usage data: pages visited, tenders saved or viewed, and similar in-app activity, used to improve matching and the product generally. Information collected through the public tender submission form is described in section 5.`,
  },
  {
    h: "3. How we use your information",
    body: `To create and secure your account (authentication uses signed tokens; your session token is kept in your browser's local storage, and your refresh token is kept in an httpOnly cookie that JavaScript cannot read). To match and rank tenders against your stated categories, states, and industry. To send the deadline alerts you've opted into, by email and/or WhatsApp. To process subscription payments and manage billing. To respond to support requests. To maintain audit logs of account activity (logins, plan changes) for security purposes.`,
  },
  {
    h: "4. Who we share information with",
    body: `We do not sell your data. We share the minimum necessary information with: Paystack, to process subscription payments; Termii, to deliver WhatsApp deadline alerts to the number you provide; and infrastructure providers hosting the platform. We may disclose information if required by Nigerian law or a valid legal process. We do not share your company's tracked tenders, saved items, or usage activity with other companies using the platform.`,
  },
  {
    h: "5. Tender submissions (no account needed)",
    body: `Anyone can submit a tender through our public submission form without creating an account. If you do, we collect your name, organisation, email address and optional phone number; the tender details you enter (title, description, category, issuing agency, location, value, deadline, requirements, eligibility, and any bid-inquiry contact email or phone number); and your IP address, which we record only to detect and prevent abuse. We use this information to review your submission, to email you about its outcome, and to protect the platform from spam. Every submission is reviewed by our team before anything is published. If a submission is approved, the tender details you entered — including any bid-inquiry contact email or phone number — are published as a tender listing on ProTrackNG. Your name, submitter email address, phone number and IP address are not published; they are visible only to ProTrackNG administrators. We keep submissions, including rejected ones, for as long as reasonably necessary for review, record-keeping and abuse prevention.`,
  },
  {
    h: "6. Data security",
    body: `Data is encrypted in transit (HTTPS) and passwords are hashed, never stored in plain text. Access to production data is restricted. No online system is perfectly secure, and we can't guarantee absolute security, but we treat account and company data as sensitive and restrict internal access accordingly.`,
  },
  {
    h: "7. Data retention",
    body: `We retain account and company data for as long as your account is active. If you close your account, we retain data only as long as reasonably necessary for legal, accounting, or dispute-resolution purposes, then delete or anonymise it.`,
  },
  {
    h: "8. Your rights",
    body: `You can view and update most of your company and preference data directly from Profile and Settings at any time. You may request a copy of the personal data we hold about you, request correction of inaccurate data, or request account deletion, by contacting us through the Support page. We will respond within a reasonable time and in line with applicable Nigerian data protection law.`,
  },
  {
    h: "9. Cookies & local storage",
    body: `ProTrackNG uses browser local storage to keep you signed in between visits (an access token and basic profile info) and an httpOnly cookie for session refresh. We do not currently use third-party advertising or tracking cookies.`,
  },
  {
    h: "10. Children's privacy",
    body: `ProTrackNG is a business tool intended for registered companies and is not directed at, or knowingly used by, children under 18.`,
  },
  {
    h: "11. Changes to this policy",
    body: `We may update this policy as the product evolves. Material changes will be announced in-app or by email before they take effect.`,
  },
  {
    h: "12. Contact",
    body: `Questions about this policy, or requests relating to your data, can be sent via the Support page once logged in, or — if you don't have an account, for example because you submitted a tender through our public form — by email to support@protrackng.com.ng.`,
  },
];

export default function Privacy() {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: "100vh", background: "#080d1a", color: "#e8eaf0", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: "760px", margin: "0 auto", padding: "56px 24px 100px" }}>
        <div
          style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "40px", cursor: "pointer", width: "fit-content" }}
          onClick={() => navigate("/")}
        >
          <ProTrackMark size={30} />
          <span style={{ fontSize: "18px", fontWeight: "800", color: "#fff" }}>
            ProTrack<span style={{ color: "#00d4a1" }}>NG</span>
          </span>
        </div>

        <h1 style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: "900", letterSpacing: "-1px", fontFamily: "'DM Serif Display', Georgia, serif", marginBottom: "8px" }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: "13px", color: "rgba(232,234,240,0.4)", marginBottom: "40px" }}>
          Last updated: {LAST_UPDATED}
        </p>

        {SECTIONS.map((s, i) => (
          <div key={i} style={{ marginBottom: "32px" }}>
            <h2 style={{ fontSize: "16px", fontWeight: "800", color: "#fff", marginBottom: "10px" }}>{s.h}</h2>
            <p style={{ fontSize: "14px", color: "rgba(232,234,240,0.65)", lineHeight: "1.8" }}>{s.body}</p>
          </div>
        ))}

        <div style={{ marginTop: "48px", paddingTop: "24px", borderTop: "1px solid rgba(232,234,240,0.08)" }}>
          <a href="#/terms" style={{ fontSize: "13px", color: "#00d4a1", textDecoration: "none", fontWeight: "600" }}>
            Read the Terms of Use →
          </a>
        </div>
      </div>
    </div>
  );
}
